import { createFileRoute } from "@tanstack/react-router";
import React, { useState } from "react";
import { Link } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import AuthLayout, { AuthHeading } from "@/components/layout/AuthLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Field, PasswordInput } from "@/components/forms";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/auth/reset-password/")({
  component: ResetPassword,
});

function passwordStrength(password: string) {
  if (password.length === 0) return { label: "", tone: "", steps: 0 };
  if (password.length < 8) return { label: "Weak", tone: "bg-destructive", steps: 1 };
  if (password.length < 12) return { label: "Medium", tone: "bg-warning", steps: 2 };
  return { label: "Strong", tone: "bg-success", steps: 3 };
}

function ResetPassword() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!newPassword || !confirmPassword) {
      setError("Please fill in all fields");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    setTimeout(() => {
      setDone(true);
      setLoading(false);
    }, 1000);
  };

  const strength = passwordStrength(newPassword);

  if (done) {
    return (
      <AuthLayout>
        <div className="mb-6 flex size-10 items-center justify-center rounded-md border border-success/30 bg-success/10 text-success">
          <CheckCircle2 className="size-5" />
        </div>
        <AuthHeading
          title="Password reset"
          description="Your password has been changed. You can now sign in with your new password."
        />
        <Button asChild className="w-full" size="lg">
          <Link to="/auth/login">Go to sign in</Link>
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthHeading
        title="Reset password"
        description="Create a new, strong password for your account."
      />

      {error && (
        <Alert variant="destructive" className="mb-5">
          <AlertCircle />
          <AlertDescription className="text-foreground">{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Field label="New password">
            <PasswordInput
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
          </Field>
          {newPassword && (
            <div className="flex items-center gap-3" aria-live="polite">
              <div className="grid flex-1 grid-cols-3 gap-1">
                {[1, 2, 3].map((step) => (
                  <span
                    key={step}
                    className={cn(
                      "h-1 rounded-full",
                      step <= strength.steps ? strength.tone : "bg-hairline",
                    )}
                  />
                ))}
              </div>
              <span className="w-14 text-right text-caption text-muted-foreground">
                {strength.label}
              </span>
            </div>
          )}
        </div>
        <Field label="Confirm password">
          <PasswordInput
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </Field>
        <Button
          type="submit"
          className="w-full"
          size="lg"
          loading={loading}
          disabled={!newPassword || !confirmPassword}
        >
          {loading ? "Resetting…" : "Reset password"}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Remember your password?{" "}
        <Link to="/auth/login" className="font-medium text-foreground hover:underline">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
